import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  Children,
  isValidElement,
} from 'react'
import { SearchableListProps, SearchableListRef } from './SearchableList.types'
import { Icon } from '../../atoms/Icon'
import { SearchInput, SearchInputRef } from '../../atoms/SearchInput'
import {
  Container,
  Header,
  TitleContainer,
  TitleIcon,
  CountBadge,
  CloseButton,
  SearchContainer,
  SearchBarRow,
  ActionRow,
  SearchResultsInfo,
  ListContainer,
  NoResults,
  NoResultsIcon,
  NoResultsTitle,
  NoResultsText,
  ClearSearchButton,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
} from './SearchableList.styles'
import { debounce } from './SearchableList.utils'

export const SearchableList = forwardRef<
  SearchableListRef,
  SearchableListProps
>(
  (
    {
      icon,
      title,
      count,
      headerAction,
      searchPlaceholder = 'Search...',
      initialQuery = '',
      onSearch,
      onClose,
      children,
      noResultsComponent,
      searchResultsRenderer,
      emptyStateComponent,
      className,
      style,
      searchable = true,
      closable = true,
      searchThreshold = 3,
      searchAction,
    },
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState(initialQuery)
    const [visibleCount, setVisibleCount] = useState(0)
    const [showNoResults, setShowNoResults] = useState(false)
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const originalContentsRef = useRef<Map<string, string>>(new Map())
    const searchInputRef = useRef<SearchInputRef | null>(null)

    const childrenArray = Children.toArray(children)
    const totalCount = childrenArray.length

    // Store original content for search highlighting
    useEffect(() => {
      if (searchable) {
        childrenArray.forEach((child, index) => {
          if (isValidElement(child)) {
            const props = child.props as Record<string, any>
            if (props['data-searchable']) {
              const key = `item-${index}`
              if (!originalContentsRef.current.has(key)) {
                // Store original HTML content as a simple marker
                originalContentsRef.current.set(key, 'stored')
              }
            }
          }
        })
      }
    }, [children, searchable])

    // Debounced search handler
    const debouncedSearch = useRef(
      debounce((query: string) => {
        performSearch(query)
        onSearch?.(query)
      }, 300)
    ).current

    // Handle search input change
    const handleSearchChange = (query: string) => {
      setSearchQuery(query)

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Debounce search to avoid too many updates
      debouncedSearch(query)
    }

    // Perform search filtering
    const performSearch = (query: string) => {
      if (!searchable) return

      let visible = 0
      const trimmedQuery = query.trim()

      if (!trimmedQuery) {
        visible = totalCount
        setShowNoResults(false)
      } else {
        // Count visible items based on search
        childrenArray.forEach((child) => {
          if (isValidElement(child)) {
            const props = child.props as Record<string, any>
            if (props['data-searchable']) {
              const searchText = getSearchableText(child)
              if (
                searchText.toLowerCase().includes(trimmedQuery.toLowerCase())
              ) {
                visible++
              }
            }
          }
        })

        setShowNoResults(visible === 0)
      }

      setVisibleCount(visible)
    }

    // Extract searchable text from a React element
    const getSearchableText = (element: React.ReactElement): string => {
      let text = ''

      const props = element.props as Record<string, any>

      // Extract text from props that might contain searchable content
      if (props.children) {
        text += extractTextFromChildren(props.children)
      }

      // Add other searchable props if needed
      if (props['data-search-text']) {
        text += ' ' + props['data-search-text']
      }

      return text
    }

    // Recursively extract text from React children
    const extractTextFromChildren = (children: any): string => {
      let text = ''

      Children.forEach(children, (child) => {
        if (typeof child === 'string') {
          text += child + ' '
        } else if (typeof child === 'number') {
          text += child.toString() + ' '
        } else if (isValidElement(child) && (child.props as any).children) {
          text += extractTextFromChildren((child.props as any).children)
        }
      })

      return text
    }

    // Filter and potentially highlight children based on search
    const filteredChildren =
      searchable && searchQuery.trim()
        ? childrenArray.filter((child) => {
            if (isValidElement(child)) {
              const props = child.props as Record<string, any>
              if (props['data-searchable']) {
                const searchText = getSearchableText(child)
                return searchText
                  .toLowerCase()
                  .includes(searchQuery.trim().toLowerCase())
              }
            }
            return true
          })
        : childrenArray

    // Clear search
    const clearSearch = () => {
      setSearchQuery('')
      performSearch('')
      onSearch?.('')
      searchInputRef.current?.focus()
    }

    // Focus search input
    const focusSearch = () => {
      searchInputRef.current?.focus()
    }

    // Get current search query
    const getSearchQuery = () => searchQuery

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      clearSearch,
      focusSearch,
      getSearchQuery,
    }))

    // Initialize search on mount
    useEffect(() => {
      if (searchable) {
        performSearch(searchQuery)
      }
    }, [searchable])

    // Handle keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Ctrl/Cmd + F to focus search
        if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
          e.preventDefault()
          focusSearch()
        }

        // Escape to clear search when search input is focused
        if (
          e.key === 'Escape' &&
          document.activeElement instanceof HTMLInputElement
        ) {
          if (searchQuery) {
            e.preventDefault()
            clearSearch()
          }
        }
      }

      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }, [searchQuery])

    const shouldShowSearch = searchable && totalCount >= searchThreshold
    const showSearchResults =
      shouldShowSearch && searchQuery.trim() && visibleCount < totalCount
    const showEmptyState = !searchQuery.trim() && totalCount === 0

    return (
      <Container className={className} style={style}>
        {/* Header */}
        <Header>
          <TitleContainer>
            <TitleIcon>
              <Icon name={icon} size="medium" />
            </TitleIcon>
            {title}
            {count !== undefined && <CountBadge>{count}</CountBadge>}
          </TitleContainer>
          {headerAction}
          {closable && (
            <CloseButton onClick={onClose} type="button">
              <Icon name="close" size="small" />
            </CloseButton>
          )}
        </Header>

        {/* Search Container - Progressive Disclosure */}
        {shouldShowSearch && (
          <SearchContainer>
            <SearchBarRow>
              <SearchInput
                ref={searchInputRef}
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                clearable
                autoFocus
              />
              {searchAction}
            </SearchBarRow>
          </SearchContainer>
        )}

        {/* Action Row - When search is hidden but searchAction exists */}
        {!shouldShowSearch && searchAction && (
          <ActionRow>{searchAction}</ActionRow>
        )}

        {/* Search Results Info */}
        {showSearchResults && (
          <SearchResultsInfo className="show">
            {searchResultsRenderer ? (
              searchResultsRenderer(visibleCount, totalCount)
            ) : (
              <>
                <strong>{visibleCount}</strong> result
                {visibleCount !== 1 ? 's' : ''} of {totalCount} items
              </>
            )}
          </SearchResultsInfo>
        )}

        {/* List Container */}
        <ListContainer>
          {showEmptyState
            ? emptyStateComponent || (
                <EmptyState>
                  <EmptyIcon>
                    <Icon name={icon} size="large" />
                  </EmptyIcon>
                  <EmptyTitle>No items</EmptyTitle>
                  <EmptyText>Start by adding your first item</EmptyText>
                </EmptyState>
              )
            : showNoResults
              ? noResultsComponent || (
                  <NoResults className="show">
                    <NoResultsIcon>
                      <Icon name="search" size="large" />
                    </NoResultsIcon>
                    <NoResultsTitle>No results found</NoResultsTitle>
                    <NoResultsText>Try different keywords</NoResultsText>
                    <ClearSearchButton onClick={clearSearch}>
                      Clear search
                    </ClearSearchButton>
                  </NoResults>
                )
              : filteredChildren}
        </ListContainer>
      </Container>
    )
  }
)

SearchableList.displayName = 'SearchableList'
